import React, { FC, memo } from 'react';

import { CustomListItem } from 'application/components/CustomList';
import { useNotificationContext } from 'application/providers/NotificationProvider';
import { useModalContext } from 'application/providers/ModalProvider';

import useMedia from '@zvuk-b2b/react-uikit/toolkit/useMedia';

import useSberBusinessOnlineViewModel from './useSberBusinessOnlineViewModel';
import { SberBusinessOnlineContent, SberBusinessOnlineActions } from './components';
import { SberBusinessOnlineProps } from './types';
import { TEXTS } from './texts';
import SberIDReauth from '../SberIDReauth';

import styles from './index.module.scss';

const SberBusinessOnline: FC<SberBusinessOnlineProps> = ({
  paymentMethodId,
  isSelected,
  switcherHidden,
  licensesCount,
  hasTrialLicenses,
  hasInactiveLicenses,
  hasActiveLicenses,
  renewableLicenses,
  nonRenewableLicenses,
  unpaidLicenses,
  setSelectedItemId,
}) => {
  const { openModal, isModalOpen, closeAllModals } = useModalContext();

  const notification = useNotificationContext();

  const { isIOS, isSafariDesktop, isDesktop } = useMedia();

  const {
    acceptanceAdvancedStatus,
    payerAccount,
    startSigningAcceptanceAdvancedLoading,
    acceptanceAdvancedLoading,
    sberAuthButtonVisible,
    onStartSigningAcceptanceAdvanced,
  } = useSberBusinessOnlineViewModel({
    isModalOpen,
    licensesCount,
    hasTrialLicenses,
    hasInactiveLicenses,
    hasActiveLicenses,
    renewableLicenses,
    nonRenewableLicenses,
    unpaidLicenses,
    isDesktop,
    isIos: isIOS || isSafariDesktop,
    showCreatedInfoNotification: () => {
      notification.showInfoNotification({
        children: TEXTS.SBER_BUSINESS_ONLINE_ACCEPTANCE_ADVANCED_CREATED,
        delay: 10000,
      });
    },
    showPendingInfoNotification: () => {
      notification.showInfoNotification({
        children: TEXTS.SBER_BUSINESS_ONLINE_ACCEPTANCE_ADVANCED_PENDING,
        delay: 10000,
      });
    },
    showCredsMismatchCancelledNotification: () => {
      notification.showInfoNotification({
        children: TEXTS.SBER_BUSINESS_ONLINE_CREDS_MISMATCH_CANCELLED,
        delay: 10000,
      });
    },
    showActiveTrialNotification: () => {
      notification.showInfoNotification({
        children: TEXTS.SBER_BUSINESS_ONLINE_ACCEPTANCE_ADVANCED_ACTIVE_TRIAL,
      });
    },
    showActiveNotification: () => {
      notification.showInfoNotification({
        children: TEXTS.SBER_BUSINESS_ONLINE_ACCEPTANCE_ADVANCED_ACTIVE,
      });
    },
    hideInfoNotification: notification.hideNotification,
    openModal,
    closeAllModals,
  });

  return (
    <div className={styles.container}>
      <CustomListItem
        id={paymentMethodId}
        elementId={`payment_method_${paymentMethodId}`}
        contentId="sber_payment"
        isSelected={isSelected}
        switcherHidden={switcherHidden}
        onChange={() => setSelectedItemId(paymentMethodId)}
        content={<SberBusinessOnlineContent />}
        small
        footer={
          <>
            <SberBusinessOnlineActions
              acceptanceAdvancedStatus={acceptanceAdvancedStatus}
              startSigningAcceptanceAdvancedLoading={startSigningAcceptanceAdvancedLoading}
              acceptanceAdvancedLoading={acceptanceAdvancedLoading}
              payerAccount={payerAccount}
              onStartSigningAcceptanceAdvanced={onStartSigningAcceptanceAdvanced}
            />
            <div className={styles.sberId}>{sberAuthButtonVisible && <SberIDReauth />}</div>
          </>
        }
      />
    </div>
  );
};

export default memo(SberBusinessOnline);
